import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FiFolderPlus, FiAlertTriangle, FiServer, FiBriefcase, FiCheckCircle, FiLoader,
} from "react-icons/fi";
import WyborOsoby from "./WyborOsoby";
import PrzyciskiKopiowania from "./PrzyciskiKopiowania";
import dyskiApi from "./dyskiApi";
import {
    RODZAJ_UDZIALU, ETYKIETY_UDZIALOW, sciezkaMatki, zlozNazwe, sciezkaPelna,
    grupyDoZalozenia, linkWindows, linkMac, walidujNazwe, zaproponujNazwe,
} from "./regulaNazwyDysku";
import s from "./SzufladaDysku.module.css";
import f from "./FormularzNowegoDysku.module.css";

/* ════════════════════════════════════════════════════════════════════════════════════════
   FORMULARZ ZAŁOŻENIA NOWEGO DYSKU — do okna „Utwórz nowe zgłoszenie”.

   [polecenie właściciela 2026-09-12]
   „w Utwórz nowe zgłoszenie -> poza listą dysków z zaktualizowaną listą i dodanych sharepoint,
    poza tym, stwórz formularz do stworzenia nowego dysku, z tą samą mechaniką, określenie ścieżki
    KRO_S_FSP_PRO oznacza Projects, KRO_S_FSP_DEP oznacza departments, dyski są w ścieżce:
    \\129.1.160.74\projects i \\129.1.160.174\departments"

   ── CO ZNACZY „Z TĄ SAMĄ MECHANIKĄ” ──────────────────────────────────────────────────────
   Człowiek podaje WYŁĄCZNIE nazwę zasobu i udział (Projects / departments). Wszystko pozostałe —
   nazwa techniczna z przedrostkiem, ścieżka sieciowa, nazwy obu grup AD (_RW i _RO) — jest
   SKŁADANE i pokazane NA ŻYWO, zanim cokolwiek zostanie wysłane. Wpisywanie pełnej nazwy grupy
   z palca to najprostszy sposób na literówkę, o której nikt się nie dowie, dopóki ktoś nie spróbuje
   wejść na folder.

   ── DLACZEGO KOMPONENT, A NIE KOD W OKNIE ZGŁOSZENIA ─────────────────────────────────────
   Okno zgłoszenia (`components/Tickets/NewTicketModal/MyNewTicketModal.js`) ma ponad 2000 linii
   i w tej fali pracują w nim inne obszary. Formularz jest więc SAMODZIELNY i wpina się jedną
   linią; walidacja i treść opisu zgłoszenia są funkcjami eksportowanymi z tego pliku, żeby okno
   nie musiało znać ani jednej reguły nazewnictwa.

   ⚠ SPRAWDZENIE ZAJĘTOŚCI NAZWY IDZIE NA SERWER — przeglądarka nie wie, co jest w ewidencji.
   Pytamy Z OPÓŹNIENIEM (debounce), nie po każdym znaku: pole „Biuro_Strategii” to 16 żądań,
   a audyt wydajności 2026-09-12 pokazał, że to właśnie LICZBA żądań kosztuje, nie ich treść.
   ════════════════════════════════════════════════════════════════════════════════════════ */

/** Stan początkowy formularza — eksportowany, żeby okno zgłoszenia nie powtarzało tego kształtu. */
export const PUSTY_NOWY_DYSK = {
    udzial: RODZAJ_UDZIALU.projekty,
    nazwa: "",
    nazwaWidoczna: "",
    uzasadnienie: "",
    wlascicielUserId: null,
    wlascicielNazwa: "",
    wlascicielEmail: "",
    uprawnienia: "RW",
};

/** Ile czekamy z pytaniem serwera o zajętość nazwy. */
const OPOZNIENIE_SPRAWDZENIA_MS = 450;

/**
 * Walidacja formularza — mapa `pole → komunikat`. Pusta mapa = wolno wysłać zgłoszenie.
 * Eksportowana, bo przycisk „Wyślij” w oknie zgłoszenia MUSI pytać o to samo, o co pyta ten ekran.
 */
export const walidujNowyDysk = (stan = {}) => {
    const bledy = {};

    const bladNazwy = walidujNazwe(stan.udzial, stan.nazwa);
    if (bladNazwy) bledy.nazwa = bladNazwy;

    if (!stan.udzial) bledy.udzial = "Wybierz, gdzie ma powstać zasób.";

    if (!stan.wlascicielUserId) {
        // Zasób bez właściciela-konta to zasób, o którego dostęp nie ma kogo zapytać. Lekcja z importu:
        // takich zasobów jest na produkcji kilkadziesiąt i każdy z nich to zgłoszenie, które nigdzie nie idzie.
        bledy.wlasciciel = "Wskaż właściciela — osobę z kontem w aplikacji, która będzie rozpatrywać wnioski o dostęp.";
    }

    if (!(stan.uzasadnienie || "").trim()) {
        bledy.uzasadnienie = "Napisz, do czego ten zasób ma służyć — bez tego IT nie wie, czy go zakładać.";
    }

    return bledy;
};

/** Czy mapa błędów jest pusta. */
export const nowyDyskGotowy = (stan) => Object.keys(walidujNowyDysk(stan)).length === 0;

/**
 * Treść dla zgłoszenia — HTML, bo okno zgłoszenia składa `details` z HTML-a.
 * Zawiera WSZYSTKO, co IT musi wiedzieć, żeby założyć grupy i folder bez dopytywania.
 */
export const opisNowegoDyskuHtml = (stan = {}) => {
    const techniczna = zlozNazwe(stan.udzial, stan.nazwa);
    const matka = sciezkaMatki(stan.udzial);
    const grupy = grupyDoZalozenia(stan.udzial, stan.nazwa);

    return `<h3>Wniosek o założenie nowego dysku sieciowego</h3>
<ul>
  <li><strong>Nazwa techniczna (grupa bazowa):</strong> ${techniczna}</li>
  <li><strong>Udział:</strong> ${ETYKIETY_UDZIALOW[stan.udzial] || "—"} (${matka})</li>
  <li><strong>Ścieżka folderu:</strong> ${sciezkaPelna(stan.udzial, stan.nazwa)}</li>
  <li><strong>Link Windows:</strong> ${linkWindows(matka, stan.nazwa)}</li>
  <li><strong>Link macOS:</strong> ${linkMac(matka, stan.nazwa)}</li>
  <li><strong>Grupy AD do założenia:</strong> ${grupy.join(", ") || "—"}</li>
  <li><strong>Nazwa widoczna dla pracowników:</strong> ${(stan.nazwaWidoczna || "").trim() || "(nie podano)"}</li>
  <li><strong>Właściciel (konto w aplikacji):</strong> ${stan.wlascicielNazwa || "—"}${stan.wlascicielEmail ? ` (${stan.wlascicielEmail})` : ""}</li>
  <li><strong>Potrzebne uprawnienia wnioskującego:</strong> ${stan.uprawnienia === "RO" ? "tylko odczyt (RO)" : "odczyt i zapis (RW)"}</li>
</ul>
<p><strong>Uzasadnienie:</strong> ${(stan.uzasadnienie || "").trim()}</p>`;
};

/**
 * @param {object}   props
 * @param {object}   props.wartosc    stan formularza (patrz {@link PUSTY_NOWY_DYSK})
 * @param {Function} props.onZmiana   (nowyStan) => void
 * @param {boolean}  [props.pokazBledy] czy rysować komunikaty (okno zgłoszenia włącza je po próbie wysłania)
 */
const FormularzNowegoDysku = ({ wartosc = PUSTY_NOWY_DYSK, onZmiana, pokazBledy = false }) => {
    /* `stan` w useMemo, bo inaczej powstaje NOWY obiekt w kazdym renderze, a wtedy useMemo i useCallback
       nizej przeliczaja sie zawsze — czyli nie robia tego, po co zostaly napisane. */
    const stan = useMemo(() => ({ ...PUSTY_NOWY_DYSK, ...wartosc }), [wartosc]);
    const [zajetosc, setZajetosc] = useState({ sprawdzam: false, zajeta: false, komunikat: null });
    const licznikRef = useRef(null);

    const bledy = useMemo(() => (pokazBledy ? walidujNowyDysk(stan) : {}), [pokazBledy, stan]);

    const ustaw = useCallback((pole, nowa) => {
        onZmiana?.({ ...stan, [pole]: nowa });
    }, [onZmiana, stan]);

    // ── Podgląd NA ŻYWO — liczony w przeglądarce, bez żądania HTTP ──────────────────────
    const techniczna = zlozNazwe(stan.udzial, stan.nazwa);
    const matka = sciezkaMatki(stan.udzial);
    const pelna = sciezkaPelna(stan.udzial, stan.nazwa);
    const grupy = grupyDoZalozenia(stan.udzial, stan.nazwa);
    const bladNazwy = walidujNazwe(stan.udzial, stan.nazwa);
    const propozycja = bladNazwy && stan.nazwa ? zaproponujNazwe(stan.nazwa) : "";

    // ── Zajętość nazwy — JEDYNE pytanie do serwera ──────────────────────────────────────
    useEffect(() => {
        if (licznikRef.current) clearTimeout(licznikRef.current);

        // Nazwy, która nie przechodzi walidacji, nie ma sensu sprawdzać — i tak nie da się jej wysłać.
        if (!stan.nazwa || bladNazwy) {
            setZajetosc({ sprawdzam: false, zajeta: false, komunikat: null });
            return undefined;
        }

        setZajetosc((p) => ({ ...p, sprawdzam: true }));
        licznikRef.current = setTimeout(async () => {
            try {
                const res = await dyskiApi.podgladNazwy(stan.udzial, stan.nazwa);
                setZajetosc({
                    sprawdzam: false,
                    zajeta: Boolean(res.data?.zajeta),
                    komunikat: res.data?.zajeta ? res.data?.blad || "Zasób o takiej nazwie już istnieje." : null,
                });
            } catch {
                // Brak odpowiedzi serwera NIE MOŻE udawać, że nazwa jest wolna — to by skończyło się
                // zgłoszeniem o założenie zasobu, który już jest. Mówimy wprost, że nie wiemy.
                setZajetosc({
                    sprawdzam: false,
                    zajeta: false,
                    komunikat: "Nie udało się sprawdzić, czy taka nazwa jest już zajęta — IT zweryfikuje to przy realizacji.",
                });
            }
        }, OPOZNIENIE_SPRAWDZENIA_MS);

        return () => {
            if (licznikRef.current) clearTimeout(licznikRef.current);
        };
    }, [stan.udzial, stan.nazwa, bladNazwy]);

    const dyskDoLinkow = { kind: "network", masterDiskAddress: matka, name: techniczna };

    return (
        <div className={f.formularz} data-testid="formularz-nowego-dysku">
            {/* ── Gdzie ma powstać ── */}
            <div className={s.sekcja}>
                <h3 className={s.sekcjaTytul}>Gdzie ma powstać zasób</h3>
                <div className={s.przelacznik} role="group" aria-label="Udział sieciowy">
                    {Object.values(RODZAJ_UDZIALU).map((u) => (
                        <button
                            key={u}
                            type="button"
                            className={`${s.przelacznikOpcja} ${stan.udzial === u ? s.przelacznikWybrana : ""}`}
                            onClick={() => ustaw("udzial", u)}
                            aria-pressed={stan.udzial === u}
                            data-testid={`nowy-dysk-udzial-${u}`}
                        >
                            {u === RODZAJ_UDZIALU.projekty ? <FiServer size={14} /> : <FiBriefcase size={14} />}
                            {ETYKIETY_UDZIALOW[u]}
                        </button>
                    ))}
                </div>
                <span className={s.podpowiedz}>
                    Przedrostek nazwy i ścieżka sieciowa wynikają z tego wyboru:
                    {" "}<code>KRO_S_FSP_PRO_</code> → <code>\\129.1.160.74\projects</code>,
                    {" "}<code>KRO_S_FSP_DEP_</code> → <code>\\129.1.160.174\departments</code>.
                </span>
            </div>

            {/* ── Nazwa ── */}
            <div className={s.sekcja}>
                <h3 className={s.sekcjaTytul}>Nazwa zasobu</h3>

                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor="nowy-dysk-nazwa">
                        Nazwa (bez przedrostka)<span className={s.wymagane}>*</span>
                    </label>
                    <div className={f.poleZPrzedrostkiem}>
                        <span className={f.przedrostek} aria-hidden="true">
                            {stan.udzial === RODZAJ_UDZIALU.projekty ? "KRO_S_FSP_PRO_" : "KRO_S_FSP_DEP_"}
                        </span>
                        <input
                            id="nowy-dysk-nazwa"
                            type="text"
                            className={`${s.wejscie} ${f.wejscieNazwy} ${(pokazBledy && bledy.nazwa) || zajetosc.zajeta ? s.wejscieBledne : ""}`}
                            value={stan.nazwa}
                            onChange={(e) => ustaw("nazwa", e.target.value)}
                            placeholder="np. Biuro_Strategii"
                            aria-invalid={(pokazBledy && bledy.nazwa) || zajetosc.zajeta ? "true" : undefined}
                            aria-describedby="nowy-dysk-nazwa-podglad"
                            data-testid="nowy-dysk-nazwa"
                        />
                    </div>

                    {/* Komunikat walidacji pokazujemy OD RAZU (nie po próbie wysłania): składanie nazwy
                        to jedyna czynność w tym formularzu, w której łatwo zrobić błąd, i człowiek ma
                        go zobaczyć, póki jeszcze patrzy na pole. */}
                    {bladNazwy && stan.nazwa && (
                        <span className={s.komunikatBledu} role="alert" data-testid="nowy-dysk-blad-nazwy">
                            <FiAlertTriangle size={12} /> {bladNazwy}
                            {propozycja && propozycja !== stan.nazwa && (
                                <button
                                    type="button"
                                    className={f.propozycja}
                                    onClick={() => ustaw("nazwa", propozycja)}
                                    data-testid="nowy-dysk-propozycja"
                                >
                                    Użyj „{propozycja}”
                                </button>
                            )}
                        </span>
                    )}
                    {pokazBledy && bledy.nazwa && !stan.nazwa && (
                        <span className={s.komunikatBledu} role="alert">
                            <FiAlertTriangle size={12} /> {bledy.nazwa}
                        </span>
                    )}
                </div>

                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor="nowy-dysk-nazwa-widoczna">
                        Nazwa widoczna dla pracowników
                    </label>
                    <input
                        id="nowy-dysk-nazwa-widoczna"
                        type="text"
                        className={s.wejscie}
                        value={stan.nazwaWidoczna}
                        onChange={(e) => ustaw("nazwaWidoczna", e.target.value)}
                        placeholder="np. Dysk Biura Strategii"
                    />
                    <span className={s.podpowiedz}>
                        Pokazywana zamiast nazwy technicznej na liście dysków. Tu spacje są dozwolone.
                    </span>
                </div>
            </div>

            {/* ── PODGLĄD NA ŻYWO ── */}
            <div className={s.sekcja}>
                <h3 className={s.sekcjaTytul}>Tak będzie wyglądał zasób</h3>

                <div className={f.podglad} id="nowy-dysk-nazwa-podglad" data-testid="nowy-dysk-podglad">
                    <div className={f.podgladWiersz}>
                        <span className={f.podgladEtykieta}>Nazwa techniczna</span>
                        <code className={f.podgladWartosc} data-testid="nowy-dysk-techniczna">
                            {techniczna || "—"}
                        </code>
                    </div>
                    <div className={f.podgladWiersz}>
                        <span className={f.podgladEtykieta}>Ścieżka folderu</span>
                        <code className={f.podgladWartosc} data-testid="nowy-dysk-sciezka">
                            {pelna || "—"}
                        </code>
                    </div>
                    <div className={f.podgladWiersz}>
                        <span className={f.podgladEtykieta}>Grupy AD do założenia</span>
                        <code className={f.podgladWartosc} data-testid="nowy-dysk-grupy">
                            {grupy.join("  ·  ") || "—"}
                        </code>
                    </div>

                    {stan.nazwa && !bladNazwy && (
                        <div className={f.podgladLinki}>
                            <span className={f.podgladEtykieta}>Linki do skopiowania</span>
                            <PrzyciskiKopiowania dysk={dyskDoLinkow} />
                        </div>
                    )}

                    {/* Zajętość nazwy — jedyna rzecz, o którą pytamy serwer. */}
                    {zajetosc.sprawdzam && (
                        <div className={f.podgladStan}>
                            <FiLoader className={s.obrot} size={12} /> Sprawdzam, czy taka nazwa jest wolna...
                        </div>
                    )}
                    {!zajetosc.sprawdzam && zajetosc.zajeta && (
                        <div className={`${f.podgladStan} ${f.podgladStanBlad}`} role="alert" data-testid="nowy-dysk-zajeta">
                            <FiAlertTriangle size={12} /> {zajetosc.komunikat}
                        </div>
                    )}
                    {!zajetosc.sprawdzam && !zajetosc.zajeta && zajetosc.komunikat && (
                        <div className={f.podgladStan} role="status">
                            <FiAlertTriangle size={12} /> {zajetosc.komunikat}
                        </div>
                    )}
                    {!zajetosc.sprawdzam && !zajetosc.zajeta && !zajetosc.komunikat && stan.nazwa && !bladNazwy && (
                        <div className={`${f.podgladStan} ${f.podgladStanOk}`} role="status" data-testid="nowy-dysk-wolna">
                            <FiCheckCircle size={12} /> Nazwa jest wolna.
                        </div>
                    )}
                </div>
            </div>

            {/* ── Właściciel i uprawnienia ── */}
            <div className={s.sekcja}>
                <h3 className={s.sekcjaTytul}>Właściciel i uprawnienia</h3>

                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor="nowy-dysk-wlasciciel">
                        Właściciel zasobu<span className={s.wymagane}>*</span>
                    </label>
                    <WyborOsoby
                        idPola="nowy-dysk-wlasciciel"
                        aktywne
                        wybrana={stan.wlascicielUserId
                            ? { id: stan.wlascicielUserId, firstName: stan.wlascicielNazwa, lastName: "", email: stan.wlascicielEmail }
                            : null}
                        onWybierz={(osoba) => onZmiana?.({
                            ...stan,
                            wlascicielUserId: osoba?.id ?? null,
                            wlascicielNazwa: osoba ? `${osoba.firstName || ""} ${osoba.lastName || ""}`.trim() : "",
                            wlascicielEmail: osoba?.email || "",
                        })}
                        bledny={Boolean(pokazBledy && bledy.wlasciciel)}
                        placeholder="Wyszukaj pracownika w aplikacji..."
                    />
                    {pokazBledy && bledy.wlasciciel && (
                        <span className={s.komunikatBledu} role="alert">
                            <FiAlertTriangle size={12} /> {bledy.wlasciciel}
                        </span>
                    )}
                    <span className={s.podpowiedz}>
                        Ta osoba będzie rozpatrywać wnioski o dostęp do zasobu. Jeśli jest dyrektorem działu,
                        zasób zostanie przypisany także do jej działu.
                    </span>
                </div>

                <div className={s.pole}>
                    <label className={s.etykieta}>Jakie uprawnienia potrzebujesz dla siebie</label>
                    <div className={s.przelacznik} role="group" aria-label="Uprawnienia wnioskującego">
                        <button
                            type="button"
                            className={`${s.przelacznikOpcja} ${stan.uprawnienia === "RW" ? s.przelacznikWybrana : ""}`}
                            onClick={() => ustaw("uprawnienia", "RW")}
                            aria-pressed={stan.uprawnienia === "RW"}
                        >
                            Odczyt i zapis (RW)
                        </button>
                        <button
                            type="button"
                            className={`${s.przelacznikOpcja} ${stan.uprawnienia === "RO" ? s.przelacznikWybrana : ""}`}
                            onClick={() => ustaw("uprawnienia", "RO")}
                            aria-pressed={stan.uprawnienia === "RO"}
                        >
                            Tylko odczyt (RO)
                        </button>
                    </div>
                    <span className={s.podpowiedz}>
                        IT zakłada ZAWSZE obie grupy (<code>_RW</code> i <code>_RO</code>) — to jeden zasób
                        w dwóch wariantach. Tu wybierasz tylko, do której grupy masz zostać dopisany.
                    </span>
                </div>

                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor="nowy-dysk-uzasadnienie">
                        Do czego ten zasób ma służyć<span className={s.wymagane}>*</span>
                    </label>
                    <textarea
                        id="nowy-dysk-uzasadnienie"
                        className={`${s.obszarTekstu} ${pokazBledy && bledy.uzasadnienie ? s.wejscieBledne : ""}`}
                        rows={3}
                        value={stan.uzasadnienie}
                        onChange={(e) => ustaw("uzasadnienie", e.target.value)}
                        placeholder="Kto będzie korzystał, jakie dane będą tam trzymane, od kiedy..."
                        aria-invalid={pokazBledy && bledy.uzasadnienie ? "true" : undefined}
                        data-testid="nowy-dysk-uzasadnienie"
                    />
                    {pokazBledy && bledy.uzasadnienie && (
                        <span className={s.komunikatBledu} role="alert">
                            <FiAlertTriangle size={12} /> {bledy.uzasadnienie}
                        </span>
                    )}
                </div>
            </div>

            <div className={f.stopkaInfo}>
                <FiFolderPlus size={14} />
                <span>
                    Zgłoszenie trafi do IT. Folder i obie grupy AD powstaną po akceptacji — aplikacja
                    sama nie zakłada niczego na serwerze plików.
                </span>
            </div>
        </div>
    );
};

export default FormularzNowegoDysku;
